/**
 * The page's base title and head texts on client-side navigation.
 *
 * `TitleComponent` prefixes the unread count to the base title; a page with a
 * title of its own (a custom frontend page, `src/sites`) sets it here instead
 * of writing `document.title`, so both stay in step. Null means the title the
 * server rendered.
 */
let baseTitle: string | null = null;
const listeners = new Set<() => void>();

export const getBaseTitle = (): string | null => baseTitle;

export const setBaseTitle = (title: string | null): void => {
  if (title === baseTitle) {
    return;
  }
  baseTitle = title;
  listeners.forEach((listener) => listener());
};

export const subscribeBaseTitle = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Sets `<meta name="description">`, or removes it for null. */
export const setMetaDescription = (content: string | null | undefined): void => {
  let tag = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!content) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = 'description';
    document.head.appendChild(tag);
  }
  tag.content = content;
};

/** Sets `<link rel="canonical">`, or removes it for null. */
export const setCanonical = (href: string | null): void => {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!href) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = 'canonical';
    document.head.appendChild(tag);
  }
  tag.href = href;
};
